import * as React from "react";
import { Inbox, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { STATE_SIZE_TOKENS, type StateSize } from "./stateSize";

interface EmptyStateProps {
  /** Icono lucide a mostrar dentro del anillo. Por defecto: Inbox. */
  icon?: LucideIcon;
  /** Título principal (jerarquía fuerte). */
  title: string;
  /** Texto de apoyo, orientado a la acción (1-2 líneas). Opcional. */
  description?: string;
  /**
   * CTA opcional debajo de la descripción (p. ej. un <Button>).
   * Se renderiza solo si se provee.
   */
  action?: React.ReactNode;
  /** Tamaño del bloque. `md` por defecto; `sm` para contenedores compactos. */
  size?: StateSize;
  className?: string;
}

/**
 * Estado vacío reutilizable y de alta calidad: icono enmarcado en un anillo
 * sutil, título con jerarquía, descripción orientada a la acción y un CTA
 * opcional. Funciona en claro y oscuro usando tokens del tema.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  const t = STATE_SIZE_TOKENS[size];

  return (
    <div
      role="status"
      className={cn(
        // Se funde al entrar: aparecer de golpe justo donde estaba el spinner
        // hace dudar de si la carga terminó o falló.
        "flex animate-fade-in flex-col items-center justify-center text-center",
        t.container,
        className
      )}
    >
      {/* Anillo + icono */}
      <div
        aria-hidden="true"
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full",
          "bg-primary/10 ring-1 ring-inset ring-primary/20",
          t.ring
        )}
      >
        <Icon className={cn("text-primary", t.icon)} strokeWidth={1.75} />
      </div>

      <h3
        className={cn(
          "font-medium text-foreground tracking-tight",
          t.gapAfterRing,
          t.title
        )}
      >
        {title}
      </h3>

      {description && (
        <p
          className={cn(
            "text-muted-foreground leading-relaxed",
            t.gapAfterTitle,
            t.description
          )}
        >
          {description}
        </p>
      )}

      {action && <div className={cn("flex items-center", t.gapAfterDesc)}>{action}</div>}
    </div>
  );
}
